import produce from 'immer';
import { maxBy } from 'lodash';
import { addNumbersOrNull } from '../../../../../helpers/numberHelpers';
import {
  HeatSourceFormModel,
  HeatSourceResponse,
  PipeResponse,
  PumpFormModel,
} from '../../GetDataForQuoteOrDesignForm';
import {
  JunctionTreeNode,
  ManifoldTreeNode,
  NodeState,
  PipeStats,
  PressureStats,
  PumpTreeNode,
  TreeNode,
  TreeNodeType,
  TreeNodeWithStats,
} from '../treeNode';
import {
  getFlowRate,
  getManifoldFlowRate,
  RoomStatsForTree,
  ROOT_ID,
  StatsData,
  Tree,
  TreeWithStats,
} from './treeHelpers';

export const getTreeWithStats = (tree: Tree, statsData: StatsData) => {
  const nodes = Object.entries(tree);
  const treeWithEmptyStats = Object.fromEntries(nodes.map(([id, node]) => [id, getNodeWithEmptyStats(node)]));

  const treeWithBottomUpStats = populateTreeWithBottomUpStats(treeWithEmptyStats, statsData, ROOT_ID);
  const treeWithTopAndBottomStats = populateTreeWithTopDownStats(
    treeWithBottomUpStats,
    statsData,
    ROOT_ID,
    null,
    null,
    null,
  );
  const treeWithPumpIndexStats = populatePumpIndexCircuitStats(treeWithTopAndBottomStats);
  return populateHeatSourceIndexCircuitStats(treeWithPumpIndexStats);
};

export const getTreeWithEmptyStats = (tree: Tree) => {
  const nodes = Object.entries(tree);
  return Object.fromEntries(nodes.map(([id, node]) => [id, getNodeWithEmptyStats(node)]));
};

const populateHeatSourceIndexCircuitStats = (tree: TreeWithStats) => {
  return produce(tree, draft => {
    const node = draft[ROOT_ID];
    if (node.nodeType === TreeNodeType.HeatSource) {
      const indexCircuitStats = getHeatSourceToPumpIndexCircuitStats(draft);
      node.stats = {
        ...node.stats,
        indexCircuitStats,
        state: getCapacityState(indexCircuitStats.pumpCapacityPercentage),
      };
    }
  });
};

const populateTreeWithBottomUpStats = (tree: TreeWithStats, statsData: StatsData, nodeId: string): TreeWithStats => {
  return produce(tree, draft => {
    recurseBottom(draft, statsData, nodeId);
  });
};

const recurseBottom = (tree: TreeWithStats, statsData: StatsData, nodeId: string) => {
  const node = tree[nodeId];

  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      const manifold = getNodeWithBottomUpStats(node, tree, statsData);
      tree[manifold.id] = manifold;
      break;

    case TreeNodeType.Pump:
    case TreeNodeType.HeatSource:
      if (node.firstChild.id) {
        recurseBottom(tree, statsData, node.firstChild.id);
      }

      const singleChildNodeWithStats = getNodeWithBottomUpStats(node, tree, statsData);
      tree[singleChildNodeWithStats.id] = singleChildNodeWithStats;
      break;

    case TreeNodeType.Junction:
      if (node.firstChild.id) {
        recurseBottom(tree, statsData, node.firstChild.id);
      }

      if (node.secondChild.id) {
        recurseBottom(tree, statsData, node.secondChild.id);
      }

      const junctionNodeWithStats: JunctionTreeNode = getNodeWithBottomUpStats(
        node,
        tree,
        statsData,
      ) as JunctionTreeNode;

      tree[junctionNodeWithStats.id] = {
        ...junctionNodeWithStats,
        fieldData: { manifoldIds: getManifoldIdsFromChildren(tree, node.firstChild.id, node.secondChild.id) },
      };
      break;
  }

  return tree;
};

const getManifoldIdsFromChildren = (
  tree: TreeWithStats,
  firstChildId: string | null,
  secondChildId: string | null,
): Array<string> => {
  return [...getManifoldIdsFromChildId(tree, firstChildId), ...getManifoldIdsFromChildId(tree, secondChildId)];
};
const getManifoldIdsFromChildId = (tree: TreeWithStats, childId: string | null) => {
  const child = childId ? tree[childId] : null;
  if (child !== null && child.nodeType === TreeNodeType.Manifold) {
    return [child.id];
  } else if (child && child.nodeType === TreeNodeType.Junction && child.fieldData) {
    return [...child.fieldData.manifoldIds];
  }
  return [];
};
const populateTreeWithTopDownStats = (
  tree: TreeWithStats,
  statsData: StatsData,
  nodeId: string,
  pressureDropRunningTotal: number | null,
  currentGeneratedPressure: number | null,
  boilerToBlendingValvePressures: PressureStats | null,
): TreeWithStats => {
  return produce(tree, draft => {
    recurse(
      draft,
      statsData,
      nodeId,
      pressureDropRunningTotal,
      currentGeneratedPressure,
      boilerToBlendingValvePressures,
    );
  });
};

const recurse = (
  tree: TreeWithStats,
  statsData: StatsData,
  nodeId: string,
  pressureDropRunningTotal: number | null,
  currentGeneratedPressure: number | null,
  boilerToBlendingValvePressures: PressureStats | null,
): TreeWithStats => {
  const node = tree[nodeId];

  tree[nodeId] = getNodeWithTopDownStats(
    node,
    statsData,
    pressureDropRunningTotal,
    currentGeneratedPressure,
    boilerToBlendingValvePressures,
  );

  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      break;

    case TreeNodeType.HeatSource:
      if (node.firstChild.id) {
        recurse(
          tree,
          statsData,
          node.firstChild.id,
          node.stats.exitPipe.pressureDropInKilopascals,
          node.stats.generatedPressureInKilopascals,
          boilerToBlendingValvePressures,
        );
      }
      break;

    case TreeNodeType.Pump:
      if (node.firstChild.id) {
        const reachedBlendingValve = !boilerToBlendingValvePressures && !!node.fieldData.includesBlendingValve;
        const blendingValvePressures = reachedBlendingValve
          ? {
              requiredPressureInKilopascals: pressureDropRunningTotal,
              availablePressureInKilopascals: currentGeneratedPressure,
              pumpCapacityPercentage: getPumpCapacity(pressureDropRunningTotal, currentGeneratedPressure),
            }
          : boilerToBlendingValvePressures;

        const newPressureDropTotal = reachedBlendingValve
          ? node.stats.exitPipe.pressureDropInKilopascals
          : addNumbersOrNull(node.stats.exitPipe.pressureDropInKilopascals, pressureDropRunningTotal);

        const newGeneratedPressure = reachedBlendingValve
          ? node.stats.generatedPressureInKilopascals
          : addNumbersOrNull(node.stats.generatedPressureInKilopascals, currentGeneratedPressure);

        recurse(
          tree,
          statsData,
          node.firstChild.id,
          newPressureDropTotal,
          newGeneratedPressure,
          blendingValvePressures,
        );
      }
      break;

    case TreeNodeType.Junction:
      if (node.firstChild.id) {
        recurse(
          tree,
          statsData,
          node.firstChild.id,
          addNumbersOrNull(pressureDropRunningTotal, node.stats.firstChildExitPipe.pressureDropInKilopascals),
          currentGeneratedPressure,
          boilerToBlendingValvePressures,
        );
      }
      if (node.secondChild.id) {
        recurse(
          tree,
          statsData,
          node.secondChild.id,
          addNumbersOrNull(pressureDropRunningTotal, node.stats.secondChildExitPipe.pressureDropInKilopascals),
          currentGeneratedPressure,
          boilerToBlendingValvePressures,
        );
      }
      break;
  }

  return tree;
};

const getNodeWithEmptyStats = (node: TreeNode): TreeNodeWithStats => {
  const emptyExitPipeStats: PipeStats = {
    velocityInMetresPerSecond: null,
    pressureDropInKilopascals: null,
    flowRateInLitresPerMinute: null,
    state: 'OK',
  };

  switch (node.nodeType) {
    case TreeNodeType.HeatSource:
      return {
        ...node,
        stats: {
          generatedPressureInKilopascals: null,
          exitPipe: emptyExitPipeStats,
          indexCircuitStats: BLANK_PRESSURE_STATS,
          state: 'OK',
        },
      };

    case TreeNodeType.Junction:
      return {
        ...node,
        stats: {
          flowRateInLitresPerMinute: null,
          firstChildExitPipe: emptyExitPipeStats,
          secondChildExitPipe: emptyExitPipeStats,
        },
      };

    case TreeNodeType.Pump:
      return {
        ...node,
        stats: {
          generatedPressureInKilopascals: null,
          exitPipe: emptyExitPipeStats,
          indexCircuitStats: BLANK_PRESSURE_STATS,
          state: 'OK',
        },
      };

    case TreeNodeType.Manifold:
      return {
        ...node,
        stats: {
          flowRateInLitresPerMinute: null,
          pressureDropInKilopascals: null,
          pumpToUfh: {
            requiredPressureInKilopascals: null,
            availablePressureInKilopascals: null,
            pumpCapacityPercentage: null,
          },
          boilerToBlendingValve: null,
          state: 'OK',
        },
      };
  }
};

const getNodeWithTopDownStats = (
  node: TreeNodeWithStats,
  statsData: StatsData,
  pressureDropRunningTotal: number | null,
  currentGeneratedPressure: number | null,
  boilerToBlendingValvePressures: PressureStats | null,
): TreeNodeWithStats => {
  switch (node.nodeType) {
    case TreeNodeType.HeatSource:
    case TreeNodeType.Junction:
      return node;

    case TreeNodeType.Manifold:
      const pumpFormModel =
        node.fieldData.manifoldIndex !== null ? statsData.pumpsByManifoldIndex[node.fieldData.manifoldIndex] : null;

      const newBoilerToBlendingValvePressures =
        !boilerToBlendingValvePressures && node.fieldData.pumpOnManifold
          ? {
              requiredPressureInKilopascals: pressureDropRunningTotal,
              availablePressureInKilopascals: currentGeneratedPressure,
              pumpCapacityPercentage: getPumpCapacity(pressureDropRunningTotal, currentGeneratedPressure),
            }
          : boilerToBlendingValvePressures;

      const PumpToManifoldPressures = getManifoldPressureStats(
        node,
        boilerToBlendingValvePressures,
        pumpFormModel,
        pressureDropRunningTotal,
        currentGeneratedPressure,
      );

      return {
        ...node,
        stats: {
          ...node.stats,
          pumpToUfh: PumpToManifoldPressures,
          boilerToBlendingValve: newBoilerToBlendingValvePressures,
        },
      };

    case TreeNodeType.Pump:
      return {
        ...node,
        stats: {
          ...node.stats,
        },
      };
  }
};

const getNodeWithBottomUpStats = (
  node: TreeNodeWithStats,
  tree: TreeWithStats,
  statsData: StatsData,
): TreeNodeWithStats => {
  const rootNode = tree[ROOT_ID];
  if (rootNode.nodeType !== TreeNodeType.HeatSource) {
    return node;
  }

  const heatSource =
    statsData.referenceData.heatSourceOptions.find(hs => hs?.displayName === rootNode.fieldData.heatSource) ?? null;

  const heatSourcePump =
    heatSource != null ? (heatSource as HeatSourceFormModel).pump ?? (heatSource as HeatSourceResponse) : null;

  switch (node.nodeType) {
    case TreeNodeType.Manifold:
      const manifoldIndex = node.fieldData.manifoldIndex;
      const duplicateManifolds =
        Object.values(tree).filter(
          treeNode =>
            treeNode.nodeType === TreeNodeType.Manifold &&
            treeNode.fieldData.manifoldIndex === node.fieldData.manifoldIndex,
        ).length > 1;

      const manifoldState = duplicateManifolds
        ? 'Error'
        : node.fieldData.pumpOnManifold
        ? getCapacityState(node.stats.pumpToUfh.pumpCapacityPercentage)
        : 'OK';

      const stats = {
        ...node.stats,
        flowRateInLitresPerMinute: statsData.rooms
          ? getManifoldFlowRate(statsData.rooms.filter(room => room.manifoldIndex === manifoldIndex))
          : null,
        pressureDropInKilopascals: getPressureDropForManifold(statsData.rooms, manifoldIndex),
        state: manifoldState,
      };

      return { ...node, stats };

    case TreeNodeType.Pump:
      const pumpPipe =
        statsData.referenceData.pipeOptions.find(pipe => pipe?.code === node.firstChild.exitPipe.code) ?? null;
      const pumpPipeStats = getExitPipeStats(tree, pumpPipe, node.id, true);

      const pump = statsData.referenceData.unitPumpOptions.find(p => p?.displayName === node.fieldData.pump) ?? null;

      const hasInadequateFlowRate =
        pump?.includesBlendingValve &&
        pumpPipeStats.flowRateInLitresPerMinute &&
        pumpPipeStats.flowRateInLitresPerMinute < 24;

      const pumpState: NodeState = hasInadequateFlowRate ? 'Error' : 'OK';

      const pumpStats = {
        ...node.stats,
        generatedPressureInKilopascals: getGeneratedPressure(pump, getFlowRate(tree, node.id)),
        exitPipe: pumpPipeStats,
        state: pumpState,
      };

      return { ...node, stats: pumpStats };

    case TreeNodeType.HeatSource:
      const heatSourcePipe =
        statsData.referenceData.pipeOptions.find(pipe => pipe?.code === node.firstChild.exitPipe.code) ?? null;
      const heatSourcePipeStats = getExitPipeStats(tree, heatSourcePipe, node.id, true);

      return {
        ...node,
        stats: {
          generatedPressureInKilopascals: getGeneratedPressure(heatSourcePump, getFlowRate(tree, node.id)),
          exitPipe: heatSourcePipeStats,
          indexCircuitStats: BLANK_PRESSURE_STATS,
          state: 'OK',
        },
      };

    case TreeNodeType.Junction:
      const firstChildPipe =
        statsData.referenceData.pipeOptions.find(pipe => pipe?.code === node.firstChild.exitPipe.code) ?? null;
      const secondChildPipe =
        statsData.referenceData.pipeOptions.find(pipe => pipe?.code === node.secondChild.exitPipe.code) ?? null;

      const firstChildPipeStats = getExitPipeStats(tree, firstChildPipe, node.id, true);
      const secondChildPipeStats = getExitPipeStats(tree, secondChildPipe, node.id, false);
      const junctionFlowRate = getFlowRate(tree, node.id);

      return {
        ...node,
        stats: {
          flowRateInLitresPerMinute: junctionFlowRate,
          firstChildExitPipe: firstChildPipeStats,
          secondChildExitPipe: secondChildPipeStats,
        },
      };
  }
};

const getManifoldPressureStats = (
  node: ManifoldTreeNode,
  boilerToBlendingValvePressures: PressureStats | null,
  pumpFormModel: PumpFormModel | null,
  pressureDropRunningTotal: number | null,
  currentGeneratedPressure: number | null,
): PressureStats => {
  if (!boilerToBlendingValvePressures && pumpFormModel) {
    const availablePressure = getGeneratedPressure(pumpFormModel, node.stats.flowRateInLitresPerMinute);

    return {
      requiredPressureInKilopascals: node.stats.pressureDropInKilopascals,
      availablePressureInKilopascals: availablePressure,
      pumpCapacityPercentage: getPumpCapacity(node.stats.pressureDropInKilopascals, availablePressure),
    };
  }

  const totalPressureDropWithManifold = addNumbersOrNull(
    node.stats.pressureDropInKilopascals,
    pressureDropRunningTotal,
  );

  return {
    requiredPressureInKilopascals: totalPressureDropWithManifold,
    availablePressureInKilopascals: currentGeneratedPressure,
    pumpCapacityPercentage: getPumpCapacity(totalPressureDropWithManifold, currentGeneratedPressure),
  };
};

const getWorstState = (...states: Array<NodeState>): NodeState => {
  if (states.includes('Error')) {
    return 'Error';
  }

  if (states.includes('Warning')) {
    return 'Warning';
  }

  return 'OK';
};

const populatePumpIndexCircuitStats = (tree: TreeWithStats): TreeWithStats => {
  return produce(tree, draft => {
    Object.values(draft).forEach(node => {
      if (node.nodeType === TreeNodeType.Pump) {
        const indexCircuitStats = getPumpToUfhIndexCircuitStatsForPump(node, tree);
        const nodeState = getWorstState(getCapacityState(indexCircuitStats.pumpCapacityPercentage), node.stats.state);
        node.stats = {
          ...node.stats,
          indexCircuitStats,
          state: nodeState,
        };
      }
    });
  });
};

const getHeatSourceToPumpIndexCircuitStats = (tree: TreeWithStats): PressureStats => {
  const nodesWithPumps = Object.values(tree).filter(
    nodeInTree => nodeInTree.nodeType === TreeNodeType.Manifold,
  ) as Array<ManifoldTreeNode>;
  return getIndexCircuitForPumpsOnAHeatSource(nodesWithPumps);
};

const getIndexCircuitForPumpsOnAHeatSource = (circuits: Array<ManifoldTreeNode>): PressureStats => {
  const stats = circuits.map(circuit => circuit.stats.boilerToBlendingValve ?? { ...BLANK_PRESSURE_STATS });
  return maxBy(stats, stat => stat.pumpCapacityPercentage) ?? { ...BLANK_PRESSURE_STATS };
};

const getPumpToUfhIndexCircuitStatsForPump = (node: PumpTreeNode, tree: TreeWithStats): PressureStats => {
  const firstChild = node.firstChild.id ? tree[node.firstChild.id] : null;
  if (firstChild === null) {
    return { ...BLANK_PRESSURE_STATS };
  }

  const manifoldsOnPump: Array<ManifoldTreeNode> =
    firstChild.nodeType === TreeNodeType.Manifold
      ? [firstChild]
      : firstChild.nodeType === TreeNodeType.Junction && firstChild.fieldData
      ? firstChild.fieldData.manifoldIds.map(manifoldId => tree[manifoldId] as ManifoldTreeNode)
      : [];

  return getIndexCircuitForManifoldsOnAPump(manifoldsOnPump);
};

const getIndexCircuitForManifoldsOnAPump = (manifolds: Array<ManifoldTreeNode>): PressureStats => {
  const pumpToUfhPressureStats: Array<PressureStats> = manifolds.map(manifold => manifold.stats.pumpToUfh);
  return maxBy(pumpToUfhPressureStats, value => value.pumpCapacityPercentage) ?? BLANK_PRESSURE_STATS;
};

export const getPressureDropForManifold = (rooms: Array<RoomStatsForTree> | null, manifoldIndex: number | null) => {
  if (!rooms || manifoldIndex === null) {
    return null;
  }

  const roomPipeDiameters = rooms
    .filter(room => room.manifoldIndex === manifoldIndex)
    .map(r => r.pipeDiameterInMillimetres);
  const roomCoilLosses = rooms.filter(r => r.manifoldIndex === manifoldIndex).map(r => r.pressureLossPerCoil ?? null);

  if (!roomPipeDiameters || roomPipeDiameters.length !== roomCoilLosses.length) {
    return null;
  }

  const roomRequiredPressures = roomCoilLosses.map((coilLoss, index) => {
    const pipeDiameter = roomPipeDiameters[index];
    if (!pipeDiameter || !coilLoss) {
      return 0;
    }
    return getPressureDropForRoom(pipeDiameter, coilLoss);
  });

  return Math.min(...roomRequiredPressures);
};

const getPressureDropForRoom = (pipeDiameter: number, coilLoss: number) => {
  switch (pipeDiameter) {
    case 10:
      return coilLoss * 1.5;
    case 12:
    case 14:
    case 16:
    case 17:
      return coilLoss * 1.4;

    default:
      return 0;
  }
};

const getGeneratedPressure = (
  pressureSource: PumpFormModel | HeatSourceFormModel['pump'] | HeatSourceResponse | null,
  flowRate: number | null,
) => {
  if (
    !pressureSource ||
    !flowRate ||
    !(pressureSource.maxPressureInKilopascals != null) ||
    !(pressureSource.firstOrder != null) ||
    !(pressureSource.pressureLimitInKilopascals != null) ||
    !(pressureSource.secondOrder != null)
  ) {
    return null;
  }

  const maxPressureAfterLoss =
    pressureSource.maxPressureInKilopascals -
    flowRate * pressureSource.firstOrder -
    Math.pow(flowRate, 2) * pressureSource.secondOrder;
  return Math.min(maxPressureAfterLoss, pressureSource.pressureLimitInKilopascals);
};

const getExitPipeStats = (
  tree: TreeWithStats,
  pipe: PipeResponse | null,
  nodeId: string | null,
  isFirstChild: boolean,
): PipeStats => {
  if (!nodeId) {
    return {
      flowRateInLitresPerMinute: null,
      velocityInMetresPerSecond: null,
      pressureDropInKilopascals: null,
      state: 'OK',
    };
  }

  const parentNode = tree[nodeId];

  if (parentNode.nodeType === TreeNodeType.Manifold) {
    return {
      flowRateInLitresPerMinute: null,
      velocityInMetresPerSecond: null,
      pressureDropInKilopascals: null,
      state: 'OK',
    };
  }

  const pipeStats =
    parentNode.nodeType === TreeNodeType.Junction && !isFirstChild
      ? parentNode.secondChild.exitPipe
      : parentNode.firstChild.exitPipe;

  const flowRateNodeId =
    parentNode.nodeType !== TreeNodeType.Junction
      ? parentNode.id
      : isFirstChild
      ? parentNode.firstChild.id
      : parentNode.secondChild.id;

  const flowRate = getFlowRate(tree, flowRateNodeId);
  const velocity = getVelocity(pipe, flowRate);
  const pressureDrop = getPressureDrop(pipe, flowRate, velocity, pipeStats.lengthInMetres);

  return {
    flowRateInLitresPerMinute: flowRate,
    velocityInMetresPerSecond: velocity,
    pressureDropInKilopascals: pressureDrop,
    state: getPipeState(velocity),
  };
};

const getPipeState = (velocity: number | null): NodeState => {
  if (!velocity) {
    return 'OK';
  }

  if (velocity < 0.5) {
    return 'Warning';
  }

  if (velocity > 1.2) {
    return 'Error';
  }

  return 'OK';
};

const getCapacityState = (pumpCapacityPercentage: number | null): NodeState => {
  return pumpCapacityPercentage && pumpCapacityPercentage > 66 ? 'Error' : 'OK';
};

const getPumpCapacity = (requiredPressure: number | null, availablePressure: number | null): number | null => {
  if (!requiredPressure || !availablePressure) {
    return null;
  }

  return (-requiredPressure / availablePressure) * 100;
};

// pressure drop formula is -(2*length) * 54.1 * (velocity^2/diameter) * ((1.5/1000 * diameter) + (0.032/velocity * diameter))^0.25
// TODO: get more context on what the magic numbers are, refactor to be more readable
const getPressureDrop = (
  pipe: PipeResponse | null,
  flowRate: number | null,
  velocity: number | null,
  length: number | null,
) => {
  if (!pipe || !length || !flowRate || !velocity) {
    return null;
  }

  const diameter = pipe.internalDiameterInMillimetres;
  return (
    Math.pow(1.5 / (1000 * diameter) + 0.032 / (velocity * diameter), 0.25) *
    (Math.pow(velocity, 2) / diameter) *
    54.1 *
    // length is doubled--there has to be a pipe coming back from the manifold as well as to it
    (-2 * length)
  );
};

const getVelocity = (pipe: PipeResponse | null, flowRate: number | null): number | null => {
  if (!pipe || !flowRate) {
    return null;
  }

  return flowRate / (60000 * pipe.internalCrossSectionalAreaInMetresSquared);
};

const BLANK_PRESSURE_STATS: PressureStats = {
  requiredPressureInKilopascals: null,
  pumpCapacityPercentage: null,
  availablePressureInKilopascals: null,
};
